import Textarea from '@ramsey-design-system/textarea';
import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useWizard } from 'react-use-wizard';
import { useMatcherContext } from '../context';
import { getAnsweredString, QuestionId } from '../flow/types';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import { generateSensitiveDataSchema } from '../../../shared/utils';
import { getMatcherFormName, useTrackMatcherStepStarted } from '../utils';
import { MatcherStandardStepSubtitleText } from './MatcherStandardStepSubtitleText';
import { MatcherStandardStepTitle } from './MatcherStandardStepTitle';
import { MatcherStepWrapper } from './MatcherStepWrapper';
import { MatcherStandardNextButton } from './MatcherStandardNextButton';
import { MatcherErrorText } from './MatcherErrorText';

interface MatcherGenericTextAreaStepFields {
  text?: string;
}

interface MatcherGenericTextAreaStepProps {
  questionId: QuestionId;
  title: string;
  subtitle?: string;
  placeholder: string;
  maxLength: number;
  stepName: string;
  showSkip?: boolean;
}

function MatcherGenericTextAreaStep({
  questionId,
  title,
  subtitle,
  placeholder,
  maxLength,
  stepName,
  showSkip = true
}: Readonly<MatcherGenericTextAreaStepProps>) {
  const { nextStep, activeStep } = useWizard();
  const { matcherAnswers, updateMatcherAnswer } = useMatcherContext();
  const schema = generateSensitiveDataSchema('text');
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<MatcherGenericTextAreaStepFields>({
    defaultValues: {
      text: getAnsweredString(matcherAnswers[questionId])
    },
    resolver: yupResolver(schema)
  });

  useTrackMatcherStepStarted(activeStep + 1, stepName, title);

  async function onValidSubmit(values: MatcherGenericTextAreaStepFields) {
    const text = values.text?.trim();
    updateMatcherAnswer(
      questionId,
      text ? { status: 'answered', value: text } : { status: 'skipped' }
    );
    trackStepCompleted({
      step: activeStep + 1,
      step_name: stepName,
      step_label: title,
      answer_labels: [title],
      answer_values: [text ?? ''],
      form_name: getMatcherFormName()
    });
    await nextStep();
  }

  async function onSkip() {
    updateMatcherAnswer(questionId, { status: 'skipped' });
    trackStepCompleted({
      step: activeStep + 1,
      step_name: stepName,
      step_label: title,
      answer_labels: [title],
      answer_values: ['skip'],
      form_name: getMatcherFormName()
    });
    await nextStep();
  }

  function onInvalidSubmit(
    errors: FieldErrors<MatcherGenericTextAreaStepFields>
  ) {
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: stepName,
      step_label: title,
      error_type: 'validation',
      error_field: 'text',
      error_message: errors.text?.message ?? '',
      form_name: getMatcherFormName()
    });
  }

  return (
    <MatcherStepWrapper onSkip={showSkip ? onSkip : undefined}>
      <form
        onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
        className="flex-1 flex flex-col"
      >
        <MatcherStandardStepTitle noSubtitle={!subtitle}>
          {title}
        </MatcherStandardStepTitle>
        {subtitle && (
          <MatcherStandardStepSubtitleText>
            {subtitle}
          </MatcherStandardStepSubtitleText>
        )}
        <div className="mb-4 mt-4">
          <Textarea
            {...register('text')}
            size="md"
            maxLength={maxLength}
            id="matcher_text_area"
            placeholder={placeholder}
            data-testid="matcher_text_area"
            invalid={!!errors.text}
          />
          <MatcherErrorText errorText={errors.text?.message} />
        </div>

        <div className="mt-auto">
          <MatcherStandardNextButton />
        </div>
      </form>
    </MatcherStepWrapper>
  );
}

export { MatcherGenericTextAreaStep };
